import React, { ReactNode, createRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Box from "veneer/Box";
import Breadcrumbs, { Breadcrumb } from "veneer/Breadcrumbs";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

interface HeaderProps {
  breadcrumbs?: Breadcrumb[];
  title?: ReactNode;
}

const headerStyle = {
  backgroundColor: Colors.white,
  borderBottom: 1,
  borderColor: "divider",
  gap: 1,
  px: 4,
  py: 3,
};

const Title = styled(Typography)(({ theme }) => ({
  color: theme.header.titleColor,
  fontFamily: theme.header.titleFont,
  fontWeight: theme.header.titleFontWeight,
  letterSpacing: theme.header.titleLetterSpacing,
}));

const headerContentRef = createRef<HTMLDivElement>();
const headerTabsRef = createRef<HTMLDivElement>();

const Header = ({ breadcrumbs = [], title }: HeaderProps) => {
  const hasBreadcrumbs = breadcrumbs.length > 0;
  const hasMainContent = !!title || hasBreadcrumbs;
  const componentStyle = hasMainContent && headerStyle;

  return (
    <Stack {...componentStyle}>
      {hasMainContent && (
        <Stack gap={1}>
          {hasBreadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          {title && <Title variant="h1">{title}</Title>}
        </Stack>
      )}
      <Box ref={headerContentRef} />
      <Box ref={headerTabsRef} />
    </Stack>
  );
};

interface HeaderContentProps {
  children?: ReactNode;
}

export const HeaderContent = ({ children }: HeaderContentProps) => {
  const [element, setElement] = useState<HTMLDivElement | null>();

  useEffect(() => {
    setElement(headerContentRef.current);
  }, []);

  if (!element) {
    return null;
  }

  return createPortal(children, element);
};

interface HeaderTabsProps {
  children?: ReactNode;
}

export const HeaderTabs = ({ children }: HeaderTabsProps) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setElement(headerTabsRef.current);
  }, []);

  if (!element) {
    return null;
  }

  return createPortal(<Box mb={-headerStyle.py}>{children}</Box>, element);
};

export { Header };
export default Header;
